// src/index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import staticApiService from './services/staticApi';

// Find the root element in your public/index.html
const root = ReactDOM.createRoot(document.getElementById('root'));

// Render your App component into the root element
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

/**
 * Register service worker for PWA functionality
 */
function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/sw.js`;
      
      navigator.serviceWorker
        .register(swUrl)
        .then((registration) => {
          console.log('[App] Service Worker registered:', registration);
          
          // Check for updates periodically
          setInterval(() => {
            registration.update();
          }, 60000); // Check every minute
          
          // Handle updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker available, show update notification
                console.log('[App] New content available, reload to update');
                
                // Optionally notify user
                if (window.confirm('New version available! Reload to update?')) {
                  newWorker.postMessage({ type: 'SKIP_WAITING' });
                  window.location.reload();
                }
              }
            });
          });
        })
        .catch((error) => {
          console.error('[App] Service Worker registration failed:', error);
        });
      
      // Handle controller change (new service worker activated)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[App] New service worker activated');
      });
    });
  } else {
    console.log('[App] Service Worker not supported');
  }
}

// Register service worker
registerServiceWorker();

/**
 * Pre-cache popular states for better offline experience
 * Popular states based on typical high dog show activity
 */
async function preCachePopularStates() {
  // Wait a bit to not interfere with initial page load
  await new Promise(resolve => setTimeout(resolve, 3000));
  
  const popularStates = [
    'California',
    'New York', 
    'Texas',
    'Florida',
    'Pennsylvania',
    'Illinois',
    'Ohio',
    'Michigan',
    'New Jersey',
    'North Carolina'
  ];
  
  const currentYear = new Date().getFullYear();
  
  console.log('[PreCache] Starting to cache popular states for', currentYear);
  
  // Use requestIdleCallback if available, otherwise setTimeout
  const scheduleCache = (state, index) => {
    const cacheState = async () => {
      try {
        const breeds = await staticApiService.preloadStateData(state, currentYear);
        console.log(`[PreCache] ✓ Cached ${state} (${breeds.length} breeds)`);
      } catch (error) {
        console.log(`[PreCache] ✗ Failed to cache ${state}:`, error.message);
      }
    };
    
    if ('requestIdleCallback' in window) {
      requestIdleCallback(cacheState, { timeout: 5000 });
    } else {
      setTimeout(cacheState, index * 1000); // Stagger by 1 second
    }
  };
  
  popularStates.forEach((state, index) => {
    scheduleCache(state, index);
  });
}

// Start pre-caching after page loads
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    preCachePopularStates().catch(err => {
      console.error('[PreCache] Failed to start pre-caching:', err);
    });
  });
}

/**
 * Register background sync when user goes online
 */
async function registerBackgroundSync() {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register('sync-data');
      console.log('[App] Background sync registered');
    } catch (error) {
      console.log('[App] Background sync registration failed:', error);
    }
  } else {
    console.log('[App] Background sync not supported');
  }
}

// Register sync when coming back online
window.addEventListener('online', () => {
  console.log('[App] Connection restored, triggering background sync');
  registerBackgroundSync();
});

// Listen for sync completion messages from service worker
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data.type === 'SYNC_COMPLETE') {
      console.log('[App] Background sync completed at', new Date(event.data.timestamp));
    }
  });
}